// Controle de Acesso (A6/A5, task T2, 2026-08-12): este arquivo vivia SO na
// imagem base (nunca commitado no overlay/git deste repo) — achado ao
// investigar o gap A5 (guard de rota: usuario digitando a URL de uma tela
// bloqueada acessava direto). A funcao `validateActiveAccountRoutes` abaixo
// JA IMPLEMENTA o guard completo (usa getBlockedFeatureKeys/
// featureKeyForRouteName/firstAllowedRouteName de accessControl.js, wireado
// em router.beforeEach via routes/index.js#initalizeRouter) — nao foi preciso
// adicionar logica nova, so trazer o arquivo pro overlay/git pra parar de
// depender so da imagem base (qualquer rebuild a partir de uma base
// DIFERENTE perderia o guard silenciosamente, sem sinal nenhum de erro).
// Ver taskT2-report.md para o diagnostico completo.
import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

import {
  getBlockedFeatureKeys,
  featureKeyForRouteName,
  firstAllowedRouteName,
} from 'dashboard/constants/accessControl.js';

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;

  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: 'dashboard',
    },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  return `accounts/${accountId}/${route ? route.path : 'dashboard'}`;
};

const validateActiveAccountRoutes = (to, user) => {
  // If the current account is active, then check for the route permissions
  const accountDashboardURL = `accounts/${to.params.accountId}/dashboard`;

  // If the user is trying to access suspended route, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  const userPermissions = getUserPermissions(user, to.params.accountId);

  // A5 (guard de rota): v105: rota mapeada no catalogo do Controle de Acesso => o CATALOGO MANDA.
  // Liberada (fora do ui_blocklist) -> acesso direto, SEM checar permissions
  // por papel — e assim que marcar um painel na tela passa a CONCEDER acesso
  // (antes a trava de papel vetava mesmo com o checkbox marcado). Bloqueada
  // -> desvio (logica v52 abaixo). Rotas FORA do catalogo seguem na checagem
  // de permissions original. id 1 nunca e restringido.
  const blockedKeys = getBlockedFeatureKeys(user);
  const featureKey = featureKeyForRouteName(to.name);
  if (featureKey) {
    if (!blockedKeys.includes(featureKey)) {
      return null;
    }
    // v52: a tela e bloqueada -> precisa desviar. Se a HOME (conversas,
    // featureKey 'All') NAO esta bloqueada, mantemos o comportamento de
    // sempre (dashboard). Se a home TAMBEM esta bloqueada, mandar pro
    // dashboard loopava ("so carregando"); entao caimos na 1a tela LIBERADA.
    // profile_settings_index e a ultima rede de seguranca (sempre acessivel).
    const homeKey = featureKeyForRouteName('home');
    if (!homeKey || !blockedKeys.includes(homeKey)) {
      return accountDashboardURL;
    }
    const landing = firstAllowedRouteName(user);
    return {
      name: landing || 'profile_settings_index',
      params: { accountId: to.params.accountId },
    };
  }

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  // If the route is not accessible for the user, return to dashboard screen
  if (!isAccessible) {
    return defaultRedirectPage(to, userPermissions);
  }

  return null;
};

export const validateLoggedInRoutes = (to, user) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user);
  }

  // If the current account is not active, then redirect the user to the suspended screen
  if (to.name !== 'account_suspended') {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isNotificationRoute = routeName =>
  routeName === 'notifications_index';
